import { AtSign, Users } from '@orgus/ui/icons';
import { useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import type { TeamsState } from '@orgus/protocol';
import { Avatar, Input, cn } from '@orgus/ui';

export interface Suggestion {
  handle: string;
  label: string;
  detail: string;
  team: boolean;
}

/** People and teams whose handle or name starts a word with `q`; teams first. */
export function suggestionsFor(state: TeamsState | null, q: string, limit = 6): Suggestion[] {
  if (!state) return [];
  const query = q.toLowerCase();
  const hit = (s: string) => !query || s.toLowerCase().split(/[\s._-]+/).some((w) => w.startsWith(query)) || s.toLowerCase().startsWith(query);
  const teams = state.teams
    .filter((t) => hit(t.handle) || hit(t.name))
    .map((t) => ({ handle: t.handle, label: t.name, detail: `@${t.handle}, ${t.members.length} ${t.members.length === 1 ? 'person' : 'people'}`, team: true }));
  const people = state.people
    .filter((p) => p.login !== 'owner' && (hit(p.login) || hit(p.name)))
    .map((p) => ({ handle: p.login, label: p.name, detail: `@${p.login}`, team: false }));
  return [...teams, ...people].slice(0, limit);
}

/** The @word being typed at the end of the text, or null. */
export function mentionAtEnd(text: string): string | null {
  const m = /(?:^|\s)@([a-z0-9._-]*)$/i.exec(text);
  return m ? m[1]! : null;
}

/**
 * A text field where @ suggests people and teams. Arrow keys move through the suggestions,
 * Enter or Tab picks one; Enter with no list open submits.
 */
export function MentionInput({
  state,
  value,
  onChange,
  onSubmit,
  label,
  placeholder,
  error,
  autoFocus,
}: {
  state: TeamsState | null;
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  label: string;
  placeholder?: string;
  error?: string | null;
  autoFocus?: boolean;
}) {
  const listId = useId();
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLInputElement>(null);
  const q = mentionAtEnd(value);
  const list = useMemo(() => (q === null ? [] : suggestionsFor(state, q)), [state, q]);
  const open = list.length > 0;

  const pick = (s: Suggestion) => {
    onChange(value.replace(/@([a-z0-9._-]*)$/i, `@${s.handle} `));
    setActive(0);
    ref.current?.focus();
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      setActive((i) => (i + (e.key === 'ArrowDown' ? 1 : list.length - 1)) % list.length);
    } else if (open && (e.key === 'Enter' || e.key === 'Tab')) {
      e.preventDefault();
      pick(list[Math.min(active, list.length - 1)]!);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <div className="relative flex flex-col gap-1">
      <Input
        ref={ref}
        size="sm"
        aria-label={label}
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setActive(0);
        }}
        onKeyDown={onKey}
        icon={<AtSign />}
        error={error ?? undefined}
        autoFocus={autoFocus}
        autoComplete="off"
        spellCheck={false}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open ? `${listId}-${Math.min(active, list.length - 1)}` : undefined}
      />
      {open && (
        <ul id={listId} role="listbox" aria-label="People and teams" className="flex flex-col rounded-md border border-border bg-surface p-1">
          {list.map((s, i) => (
            <li
              key={`${s.team ? 't' : 'p'}:${s.handle}`}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(s);
              }}
              onMouseEnter={() => setActive(i)}
              className={cn('flex h-8 cursor-default items-center gap-2 rounded-sm px-2 text-sm', i === active ? 'bg-hover text-fg' : 'text-fg-muted')}
            >
              {s.team ? (
                <span className="flex size-5 items-center justify-center rounded-md bg-accent-soft text-accent">
                  <Users className="size-3" aria-hidden />
                </span>
              ) : (
                <Avatar name={s.label} colorKey={s.handle} size={20} />
              )}
              <span className="truncate text-fg">{s.label}</span>
              <span className="ml-auto truncate text-xs text-fg-subtle">{s.detail}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
